import React, {useState} from 'react'
import {Typography, Stack, Alert} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import {RootState} from '../../store'
import MainBtn from '../customTable/MainBtn'
import PopUp from '../customTable/PopUp'
import Invoice from './invoce/View'
import SimpleLoading from '../customTable/SimpleLoading'
import {formatMoneyWithSymbol} from './formatMoney'
import PaymentGatewaySelector from '../wallet/components/PaymentGatewaySelector'
import OTPModal from '../wallet/components/OTPModal'
import {handlePaymentInitiationResponse, parsePaymentAmount, formatPaymentDisplay, isBlomUnifiedCheckout, runBlomUnifiedCheckout, toIsoCurrencyCode} from '../../utils/paymentHelpers'
import ServiseFees from '../../Api/serviseFees'

type FlowStep = 'otp' | 'gateway'

export default function Row({
  item,
  i,
  switchValue,
  isWhishEnabled = true,
}: {
  item: any
  i: number
  switchValue: string
  isWhishEnabled?: boolean
}) {
  const {t} = useTranslation()
  const [openInvice, setOpenInvice] = React.useState(false)
  const [editItem] = useState(item)
  const [isLoadingPay, setLoadingPay] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [flowOpen, setFlowOpen] = useState(false)
  const [flowStep, setFlowStep] = useState<FlowStep>('otp')
  const [signature, setSignature] = useState('')
  const [otpAmountLabel, setOtpAmountLabel] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [isDeleted] = useState(true)
  const store = useSelector((state: RootState) => state.vender)

  const countryId = Number(localStorage.getItem('country_id') || 0)
  const currency = (editItem.paid_currency_symbol || localStorage.getItem('currency') || 'USD').trim()
  const amount = parsePaymentAmount(editItem.total)

  const openInvoice = () => setOpenInvice(true)

  const resetFlow = () => {
    setFlowStep('otp')
    setSignature('')
    setErrorMsg('')
    setOtpAmountLabel('')
  }

  const closeFlow = () => {
    if (isLoadingPay) {
      return
    }
    setFlowOpen(false)
    resetFlow()
  }

  const startPay = () => {
    setErrorMsg('')
    ServiseFees.startOtp(
      editItem.id,
      (res: any) => {
        const nextSignature = res?.signature ?? res?.data?.signature
        if (!nextSignature) {
          setErrorMsg(String(t('paymentGatewaysLoadError') || 'Unable to start verification'))
          return
        }
        const nextAmount = parsePaymentAmount(res?.amount ?? res?.data?.amount ?? amount)
        const nextCurrency = String(res?.currency ?? res?.data?.currency ?? currency).trim()
        setSignature(String(nextSignature))
        setOtpAmountLabel(formatPaymentDisplay(nextAmount, nextCurrency))
        setFlowStep('otp')
        setFlowOpen(true)
      },
      setLoadingPay
    )
  }

  const handleOtpSubmit = (code: string) => {
    setErrorMsg('')
    ServiseFees.confirmOtp(
      {signature, code, service_fee_id: editItem.id},
      () => setFlowStep('gateway'),
      setLoadingPay
    )
  }

  const handleGatewaySelected = (factoryName: string) => {
    const currency_code = toIsoCurrencyCode(currency)

    if (isBlomUnifiedCheckout(factoryName)) {
      runBlomUnifiedCheckout(
        {
          purpose: 'service_fee',
          service_fee_id: editItem.id,
          currency_code,
        },
        setLoadingPay,
        (message) => setErrorMsg(message)
      )
      return
    }

    ServiseFees.payByGateway(
      factoryName,
      editItem.id,
      (res: any) => {
        handlePaymentInitiationResponse(res)
      },
      setLoadingPay,
      {currency_code}
    )
  }

  return (
    <>
      {isDeleted && (
        <tr className='fw-bold text-muted '>
          <td style={{cursor: 'pointer'}}>
            <a onClick={openInvoice} style={{color: '#009EF7'}}>
              #{editItem.code}
            </a>
          </td>
          <td style={{textAlign: 'start'}}>
            <span className='text-muted fs-7 fw-semibold'>{editItem.start_date}</span>
          </td>
          <td style={{textAlign: 'start'}}>
            {formatMoneyWithSymbol(editItem.paid_currency_symbol, editItem.paid_amount)}
          </td>
          <td style={{textAlign: 'start'}}>
            {formatMoneyWithSymbol(editItem.paid_currency_symbol, editItem.total)}
          </td>
          <td style={{textAlign: 'start'}}>{editItem.due_date}</td>
          {store.permissions && store.permissions.includes('services_fees_pay') && (
            <td style={{textAlign: 'start'}} className='text-start'>
              {editItem.paid_date || editItem.paid_amount > 0 ? (
                <Typography color='#50CD89' fontSize='13px' fontWeight={600}>
                  {t('paid')}
                </Typography>
              ) : !isWhishEnabled ? (
                <Typography color='var(--bs-text-dark)' fontSize='13px'>
                  {t('noPaymentMethodAvailable')}
                </Typography>
              ) : isLoadingPay ? (
                <SimpleLoading />
              ) : (
                <MainBtn
                  disabled={editItem.paid_date}
                  text='pay'
                  width={{xs: '100%', sm: '180px'}}
                  height='52px'
                  minWidth={{xs: '100%', sm: '180px'}}
                  handelSubmit={startPay}
                />
              )}
            </td>
          )}
        </tr>
      )}

      <PopUp width='md' open={openInvice} setOpen={setOpenInvice}>
        {openInvice && <Invoice editItem={editItem} id={editItem.id} />}
      </PopUp>

      <PopUp width='sm' open={flowOpen} setOpen={setFlowOpen} isLoading={isLoadingPay}>
        <Stack p={{xs: '16px', sm: '24px'}} gap='12px' bgcolor='#fff'>
          {errorMsg ? <Alert severity='error'>{errorMsg}</Alert> : null}
          {flowStep === 'otp' ? (
            <OTPModal
              signature={signature}
              amountLabel={otpAmountLabel}
              amount={amount}
              currency={currency}
              planName={`${t('servesFees') || 'Service fees'} #${editItem.code}`}
              isLoading={isLoadingPay}
              isResending={isResending}
              onSubmit={handleOtpSubmit}
              onCancel={closeFlow}
              onResend={() => ServiseFees.resendOtp({signature}, () => {}, setIsResending)}
            />
          ) : (
            <PaymentGatewaySelector
              open={flowOpen && flowStep === 'gateway'}
              onClose={closeFlow}
              amount={amount}
              currency={currency}
              purpose='service_fee'
              countryId={countryId}
              referenceId={Number(editItem.id) || 0}
              isPaying={isLoadingPay}
              onGatewaySelected={handleGatewaySelected}
              summary={{
                titleKey: 'completePayment',
                planName: `${t('servesFees') || 'Service fees'} #${editItem.code}`,
                priceLabel: formatMoneyWithSymbol(editItem.paid_currency_symbol, editItem.total),
              }}
            />
          )}
        </Stack>
      </PopUp>
    </>
  )
}
